import type { InviteMember, RoleOption, UpdateMember } from '@cyberscore/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession, can } from '../../lib/session';
import { refreshLists } from '../../lib/list';

/** People in the tenant (task 52): members and invitations, for tenant admins. */

export interface Member { user_id: string; email: string | null; name: string | null; roles: string[]; status: 'active' | 'suspended'; created_at: string; last_seen_at: string } // prettier-ignore

/** A page of members (task 60) with the roles to hand out; open invitations all at once, as there are few. */
export interface People {
  /** Built-in roles this kind of organisation has, then its own (task 78). */
  assignable_roles: RoleOption[];
  invitations: { id: string; email: string; roles: string[]; created_at: string; expires_at: string; expired: boolean }[]; // prettier-ignore
}

export function usePeopleAccess() {
  const { tenant } = useSession();
  return can(tenant, 'people.view');
}

export function usePeopleAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      a:
        | { type: 'invite'; body: InviteMember }
        | { type: 'revoke'; id: string }
        | { type: 'update'; userId: string; body: UpdateMember },
    ) =>
      a.type === 'invite'
        ? api('POST', '/tenants/current/invitations', a.body)
        : a.type === 'revoke'
          ? api('POST', `/tenants/current/invitations/${a.id}/revoke`)
          : api('PATCH', `/tenants/current/members/${a.userId}`, a.body),
    // The answer is the first page; the page on screen may be another, so it is asked for again.
    onSuccess: () => refreshLists(qc),
  });
}
